import { useState, useRef, useCallback } from 'react';
import { TranscriptionClient } from '@/lib/transcriptionClient';
import { useToast } from './use-toast';

export interface UseVoiceInputOptions {
  onTranscriptionComplete?: (text: string) => void;
  onError?: (error: Error) => void;
}

export function useVoiceInput(options: UseVoiceInputOptions = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const currentChunkDataRef = useRef<Blob[]>([]);
  const completedChunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>('');
  const chunkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transcriptionQueueRef = useRef<Blob[]>([]);
  const isProcessingQueueRef = useRef(false);
  const accumulatedTextRef = useRef<string>('');
  const isStoppedRef = useRef(false);
  const toast = useToast();

  // Process transcription queue sequentially
  const processTranscriptionQueue = useCallback(async () => {
    if (isProcessingQueueRef.current) {
      return;
    }

    isProcessingQueueRef.current = true;
    setIsTranscribing(true);

    while (transcriptionQueueRef.current.length > 0) {
      const chunk = transcriptionQueueRef.current.shift();
      if (!chunk) continue;

      try {
        const base64Audio = await blobToBase64(chunk);
        const extension = mimeTypeRef.current.includes('webm') ? 'webm' : 'wav';
        const fileName = `chunk-${Date.now()}.${extension}`;
        
        const response = await TranscriptionClient.transcribe({
          audioData: base64Audio,
          fileName,
        });
        
        if (response.text.trim()) {
          // Call the callback immediately with just this chunk's text
          // This allows the UI to show text as it's transcribed
          if (options.onTranscriptionComplete) {
            options.onTranscriptionComplete(response.text.trim());
          }
          accumulatedTextRef.current += (accumulatedTextRef.current ? ' ' : '') + response.text.trim();
        }
      } catch (error) {
        console.error('Transcription error for chunk:', error);
        // Continue processing remaining chunks
      }
    }

    isProcessingQueueRef.current = false;
    
    // Only finish if we've stopped recording
    if (isStoppedRef.current) {
      setIsTranscribing(false);
      
      // Check if we got any text at all
      if (!accumulatedTextRef.current) {
        const error = new Error('No speech detected in recording');
        if (options.onError) {
          options.onError(error);
        }
        toast.toast({
          title: 'No speech detected',
          description: 'Please try speaking more clearly.',
          variant: 'destructive',
        });
      }
      
      // Reset for next recording
      accumulatedTextRef.current = '';
      isStoppedRef.current = false;
    }
  }, [options, toast]);

  // Stop current recorder and start a new one (to create complete audio chunks)
  const restartRecorder = useCallback(() => {
    if (!mediaRecorderRef.current || !streamRef.current || isStoppedRef.current) {
      return;
    }

    const currentRecorder = mediaRecorderRef.current;
    
    // Stop the current recorder (this will trigger onstop and save the chunk)
    if (currentRecorder.state === 'recording') {
      currentRecorder.stop();
    }
  }, []);

  const startNewRecorderChunk = useCallback(() => {
    if (!streamRef.current || isStoppedRef.current) {
      return;
    }

    const mimeType = mimeTypeRef.current;
    const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType });
    mediaRecorderRef.current = mediaRecorder;
    currentChunkDataRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        currentChunkDataRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      // Create a complete blob from this chunk's data
      if (currentChunkDataRef.current.length > 0) {
        const chunkBlob = new Blob(currentChunkDataRef.current, { type: mimeType });
        completedChunksRef.current.push(chunkBlob);
        
        // Add to transcription queue
        transcriptionQueueRef.current.push(chunkBlob);
        processTranscriptionQueue();
        
        currentChunkDataRef.current = [];
      }

      // Start a new chunk if still recording (check isStoppedRef)
      if (!isStoppedRef.current && streamRef.current) {
        startNewRecorderChunk();
      }
    };

    // Only start if not stopped
    if (!isStoppedRef.current) {
      mediaRecorder.start();
    }
  }, [processTranscriptionQueue]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Use webm format which is well-supported
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/wav';
      
      mimeTypeRef.current = mimeType;
      isStoppedRef.current = false;
      accumulatedTextRef.current = '';
      completedChunksRef.current = [];
      transcriptionQueueRef.current = [];
      
      // Start the first recording chunk
      startNewRecorderChunk();
      
      // Set up interval to restart recorder every 2 seconds
      chunkIntervalRef.current = setInterval(() => {
        restartRecorder();
      }, 2000);
      
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      const err = error instanceof Error ? error : new Error('Failed to start recording');
      if (options.onError) {
        options.onError(err);
      }
      toast.toast({
        title: 'Recording failed',
        description: 'Please ensure microphone permissions are granted.',
        variant: 'destructive',
      });
    }
  }, [options, toast, startNewRecorderChunk, restartRecorder]);

  const stopRecording = useCallback(() => {
    if (isRecording) {
      // Mark as stopped FIRST to prevent new chunks from starting
      isStoppedRef.current = true;
      
      // Clear the interval to stop automatic restarts
      if (chunkIntervalRef.current) {
        clearInterval(chunkIntervalRef.current);
        chunkIntervalRef.current = null;
      }
      
      // Stop the current recorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      
      // Stop the media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      setIsRecording(false);
    }
  }, [isRecording]);

  const cancelRecording = useCallback(() => {
    if (isRecording) {
      // Mark as stopped FIRST to prevent new chunks
      isStoppedRef.current = true;
      
      // Clear the interval
      if (chunkIntervalRef.current) {
        clearInterval(chunkIntervalRef.current);
        chunkIntervalRef.current = null;
      }
      
      // Stop the media recorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      
      // Clear all chunks and queues to prevent processing
      currentChunkDataRef.current = [];
      completedChunksRef.current = [];
      transcriptionQueueRef.current = [];
      accumulatedTextRef.current = '';
      
      // Stop the media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      setIsRecording(false);
      
      // Reset stopped flag after cleanup
      isStoppedRef.current = false;
    }
  }, [isRecording]);

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    cancelRecording,
    isProcessing: isRecording || isTranscribing,
  };
}

// Helper function to convert Blob to base64
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:audio/webm;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
